import { useEffect } from 'react';
import './App.css';
import ImprovedEnterpriseLayoutPreview from './Layout/main_layout';
import LoginInterface from './login-interface';
import { Route, Routes, useLocation } from 'react-router-dom';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { GlobalErrorHandler } from './components/common/GlobalErrorHandler';
import { TabTakeoverGuard } from './components/common/TabTakeoverGuard';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { hasActiveSession } from './auth/sessionState';
import {
  clearAllAuthStorage,
  initCrossTabLogin,
  initCrossTabLogout,
  installBackNavigationGuard,
} from './utils/authGuard';

// The authenticated shell's route. Unlike react-project-template (where login
// and the shell BOTH live at the bare root behind a RootGate), Fairbooks keeps
// the original template routing: login at `/`, shell at this path.
const SHELL_PATH = '/Layout/main_layout';

/**
 * Cross-tab auth sync + login-view Back-button trap.
 * Ported from react-project-template's `AuthLifecycle` in App.tsx, adapted to
 * Fairbooks' two-path routing (see SHELL_PATH above).
 */
function AuthLifecycle() {
  const location = useLocation();
  const authed = hasActiveSession();
  const onLoginView = location.pathname === '/';

  useEffect(() => {
    return initCrossTabLogout(() => {
      clearAllAuthStorage();
      // Reload to the login view. `.replace()` overwrites the history entry so
      // Back cannot land on a cached authenticated page.
      window.location.replace('/');
    });
  }, []);

  useEffect(() => {
    return initCrossTabLogin(() => {
      // Only hop tabs still sitting on the login screen into the app — don't
      // disturb an actively-used authenticated tab.
      if (window.location.pathname === '/') {
        window.location.replace(SHELL_PATH);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Trap the browser Back button while on the login view (e.g. post-logout)
    // so a cached authenticated page can't be reached through history.
    if (authed || !onLoginView) return;
    return installBackNavigationGuard();
  }, [authed, onLoginView, location.key]);

  return null;
}

/**
 * Mounts the single-active-tab presence guard only once authenticated.
 * Pre-auth duplicate tabs coexist freely (helpful when someone opens the URL
 * twice before signing in); enforcement kicks in the moment a session exists.
 */
function TabPresenceGate() {
  useLocation(); // subscribe to navigation so auth transitions re-render this gate
  if (!hasActiveSession()) return null;
  return <TabTakeoverGuard />;
}

function App() {
  return (
    <AuthProvider>
      <GlobalErrorHandler>
        <ErrorBoundary
          showNotification={true}
          logError={true}
          errorMessage="The application encountered an error. Please try again."
        >
          <AuthLifecycle />
          <TabPresenceGate />
          <Routes>
            <Route path="/" element={<LoginInterface />} />
            <Route
              path={SHELL_PATH}
              element={
                <ProtectedRoute>
                  <ImprovedEnterpriseLayoutPreview />
                </ProtectedRoute>
              }
            />
            {/* other routes */}
          </Routes>
        </ErrorBoundary>
      </GlobalErrorHandler>
    </AuthProvider>
  );
}

export default App;
